import { useCallback } from "react";
import { getPreparedAxiosInstance } from "./AxiosInstance";
import { AxiosPromise, AxiosRequestConfig } from "axios";
import { Config } from "../config";

export type ListResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

const AxiosInstance = getPreparedAxiosInstance();

export function useFetch() {
  const applyRequiredHeaders = useCallback((headers: object = {}) => {
    return Object.assign(headers, {
      Authorization:
        "Bearer " + window.localStorage.getItem("CURRENT_USER_TOKEN"),
      Accept: "application/json; version=v" + Config.API_VERSION,
    });
  }, []);
  const get = useCallback<
    <T>(url: string, config?: AxiosRequestConfig) => AxiosPromise<T>
  >((url, config) => {
    return AxiosInstance.get(url, {
      ...config,
      headers: applyRequiredHeaders(config?.headers ?? {}),
    });
  }, []);

  const post = useCallback<
    <T>(
      url: string,
      data?: Record<string, unknown> | Array<any> | Blob | FormData,
      config?: AxiosRequestConfig,
    ) => AxiosPromise<T>
  >((url, data = {}, config) => {
    return AxiosInstance.post(url, data, {
      ...config,
      headers: applyRequiredHeaders(config?.headers ?? {}),
    });
  }, []);

  const put = useCallback<
    <T>(
      url: string,
      data?: Record<string, unknown> | Array<any>,
      config?: AxiosRequestConfig,
    ) => AxiosPromise<T>
  >((url, data = {}, config) => {
    return AxiosInstance.put(url, data, {
      ...config,
      headers: applyRequiredHeaders(config?.headers ?? {}),
    });
  }, []);

  const patch = useCallback<
    <T>(
      url: string,
      data?: Record<string, unknown>,
      config?: AxiosRequestConfig,
    ) => AxiosPromise<T>
  >((url, data = {}, config) => {
    return AxiosInstance.patch(url, data, {
      ...config,
      headers: applyRequiredHeaders(config?.headers ?? {}),
    });
  }, []);

  const deleteRequest = useCallback<
    <T>(url: string, config?: AxiosRequestConfig) => AxiosPromise<T>
  >((url, config = {}) => {
    return AxiosInstance.delete(url, {
      ...config,
      headers: applyRequiredHeaders(config?.headers ?? {}),
    });
  }, []);

  const postFile = useCallback(
    (
      url: string,
      file: FormData | Blob | File,
      config?: AxiosRequestConfig,
    ) => {
      return AxiosInstance.put(url, file, {
        ...config,
        headers: applyRequiredHeaders({
          ...(config?.headers ?? {}),
          "Content-Type": "multipart/form-data",
        }),
      });
    },
    [],
  );

  const postAsPutFile = useCallback(
    (
      url: string,
      file: FormData | Blob | File,
      config?: AxiosRequestConfig,
    ) => {
      return AxiosInstance.post(url, file, {
        ...config,
        headers: applyRequiredHeaders({
          ...(config?.headers ?? {}),
          "Content-Type": "multipart/form-data",
        }),
      });
    },
    [],
  );

  const putAsPutFile = useCallback(
    (
      url: string,
      file: FormData | Blob | File,
      config?: AxiosRequestConfig,
    ) => {
      return AxiosInstance.put(url, file, {
        ...config,
        headers: applyRequiredHeaders({
          ...(config?.headers ?? {}),
          "Content-Type": "multipart/form-data",
        }),
      });
    },
    [],
  );

  const postAsAnon = useCallback<
    <T>(
      url: string,
      data?: Record<string, unknown>,
      config?: AxiosRequestConfig,
    ) => AxiosPromise<T>
  >((url, data = {}, config) => {
    return AxiosInstance.post(url, data, config);
  }, []);

  return {
    get,
    post,
    postFile,
    postAsPutFile,
    putAsPutFile,
    put,
    postAsAnon,
    patch,
    deleteRequest,
  };
}
